
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { Canvas, PencilBrush, Object as FabricObject, Textbox } from 'fabric';
import type { Point, TPointerEvent } from 'fabric';
import { EraserBrush } from '@erase2d/fabric';
import BaseSlider from '@/components/base/BaseSlider.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import ColorInput from '@/components/ColorInput.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import ComplexBackground from '@/components/designElements/ComplexBackground.vue';
import { v4 as uuidv4 } from 'uuid';
import { functions, Timestamp } from '@/firebase';
import {
	ANNOTATION_CLIENT_TIMEOUT_MS,
	AnnotationSaveRequest,
	AnnotationSaveResponse,
	RUNNIT_NODE_FIELD_TYPE,
	RunnitUpload,
	extractPromptStringValue,
} from '@run-diffusion/shared';
import { disposeFabricCanvasSafely, getFabricCanvasContainer, restoreFabricCanvasElement } from './canvasLifecycle';
import {
	ANNOTATION_SAVE_FAILURE,
	AnnotationSaveError,
	getAnnotationSaveErrorMessage,
	toAnnotationSaveError,
} from './annotationSaveErrors';
import {
	ANNOTATION_OBJECT_DEFAULTS,
	TEXT_OBJECT_DEFAULTS,
	TOOL,
	TOOLS,
	TOOL_ORDER,
	ToolContext,
	ToolId,
	TextOrigin,
	applyToolToCanvas,
	createToolGestureState,
	fitTextboxWidth,
} from './annotationTools';
import {
	AnnotationHistoryState,
	canRedo as historyCanRedo,
	canUndo as historyCanUndo,
	createAnnotationHistoryState,
	recordSnapshot as recordHistorySnapshot,
	redoSnapshot,
	resetHistory,
	undoSnapshot,
} from './annotationHistory';
import { analytics, EVENTS } from '@/services/analytics';

/** Fully opaque: the alpha of an EraserBrush colour is its erase strength. */
const ERASER_BRUSH_COLOR = 'rgba(0, 0, 0, 1)';

export default Vue.extend({
	name: 'VisualPromptEditor',
	props: {
		backgroundImageUrl: { type: String, default: null },
		fieldUuid: { type: String, required: true },
		originalImage: { type: Object, default: null },
	},
	data () {
		return {
			TOOL,
			STATIC_HEIGHT: 550,
			STATIC_WIDTH: 550,
			MOBILE_STATIC_HEIGHT: 300,
			MOBILE_STATIC_WIDTH: 300,
			LARGE_STATIC_HEIGHT: 800,
			LARGE_STATIC_WIDTH: 800,

			selectedTool: TOOL.PEN as ToolId,
			selectedColor: '#ffffff',
			penSize: 5,
			canvasInstanceId: uuidv4(),
			canvas: null as Canvas | null,
			historyState: createAnnotationHistoryState() as AnnotationHistoryState,
			pencilBrush: null as PencilBrush | null,
			eraserBrush: null as EraserBrush | null,
			// EraserBrush.on() hands back a disposer rather than a canvas.off()
			// style handle, so teardown has to keep them.
			eraserDisposers: [] as (() => void)[],

			hasResizeListener: false,
			imageWidth: 0,
			imageHeight: 0,
			canvasWidth: 550,
			canvasHeight: 550,
			isDrawing: false,
			isInitializing: false,

			gesture: createToolGestureState(),
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
		]),

		canvasId () {
			return `visual-prompt-canvas-${this.fieldUuid || 'default'}-${this.canvasInstanceId}`;
		},
		fieldEditorState () {
			const fieldUuid: string = this.fieldUuid || 'default';
			return this.runnitState.visualPromptEditorStates[fieldUuid];
		},
		isReplacementMode (): boolean {
			return !!this.fieldEditorState.replaceImageTarget;
		},
		canUndo (): boolean {
			return historyCanUndo(this.historyState);
		},
		canRedo (): boolean {
			return historyCanRedo(this.historyState);
		},
		toolButtons (): { id: ToolId, icon: string, label: string }[] {
			const meta: Record<ToolId, { icon: string, label: string }> = {
				[TOOL.MOVE]: { icon: 'mdi-cursor-move', label: 'Move' },
				[TOOL.PEN]: { icon: 'mdi-grease-pencil', label: 'Pen' },
				[TOOL.TEXT]: { icon: 'mdi-format-text', label: 'Text' },
				[TOOL.SQUARE]: { icon: 'mdi-square-outline', label: 'Square' },
				[TOOL.CIRCLE]: { icon: 'mdi-circle-outline', label: 'Circle' },
				[TOOL.ARROW]: { icon: 'mdi-arrow-top-right', label: 'Arrow' },
				[TOOL.ERASER]: { icon: 'mdi-eraser', label: 'Eraser' },
			};
			return TOOL_ORDER.map((id: ToolId) => ({ id, ...meta[id] }));
		},
		undoTooltip (): string {
			return 'Undo (Cmd/Ctrl + Z)';
		},
		redoTooltip (): string {
			return 'Redo (Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y)';
		},
		hasOriginalImage (): boolean {
			return !!(
				this.originalImage &&
				(this.originalImage.nodeRunResult || this.originalImage.upload)
			);
		},
	},
	watch: {
		penSize: {
			handler (newSize: number) {
				// Pen and Eraser share the one visible size control.
				if (this.pencilBrush) this.pencilBrush.width = newSize;
				if (this.eraserBrush) this.eraserBrush.width = newSize;
			},
		},
		selectedColor: {
			handler (newColor: string) {
				// Deliberately not applied to the eraser: its colour's alpha is
				// erase strength, so a non-opaque pick would silently produce a
				// partial erase.
				if (this.pencilBrush) {
					this.pencilBrush.color = this.hexToRgba(newColor);
				}
				if (this.canvas) {
					const activeObject = this.canvas.getActiveObject();
					if (activeObject && activeObject.type === 'textbox') {
						(activeObject as Textbox).set('fill', newColor);
						this.canvas.renderAll();
						this.recordSnapshot();
					}
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateVisualPromptEditorState',
			'updateSnackbar',
			'removeVisualPromptEditorState',
		]),
		hexToRgba (hex: string, alpha: number = 1): string {
			const r = parseInt(hex.slice(1, 3), 16);
			const g = parseInt(hex.slice(3, 5), 16);
			const b = parseInt(hex.slice(5, 7), 16);
			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		},
		initCanvas () {
			// Prevent concurrent initialization
			if (this.isInitializing) {
				return;
			}

			if (this.canvas) {
				console.warn('Canvas already exists, skipping initialization');
				return;
			}

			const canvasElement = this.$refs.canvasRef as HTMLCanvasElement | undefined;
			if (!canvasElement) {
				console.warn('Canvas element not found, skipping initialization');
				return;
			}

			// Set flag to prevent concurrent initialization
			this.isInitializing = true;

			this.destroyCanvas();

			// Fabric wraps the editor canvas on initialization; repair stale wrappers before recreating it.
			const hasLowerCanvas = canvasElement.parentElement?.querySelector(`canvas.lower-canvas`);

			if (hasLowerCanvas) {
				restoreFabricCanvasElement(canvasElement);
			}

			this.setCanvasHeight();
			this.setCanvasWidth();

			try {
				this.canvas = new Canvas(canvasElement, {
					width: this.canvasWidth,
					height: this.canvasHeight,
					isDrawingMode: this.selectedTool === TOOL.PEN,
				});
			} catch (error) {
				this.isInitializing = false;
				throw error;
			}

			if (this.canvas) {
				this.setupBrushes();
				applyToolToCanvas(this.canvas, this.selectedTool);

				// Every pointer event routes through the active tool definition.
				// The old inline conditionals mixed which tool was active with
				// whether a drag was running with whether history was replaying.
				this.canvas.on('mouse:move', (event) => {
					const pointer = this.canvas.getPointer(event.e);
					const tool = TOOLS[this.selectedTool];
					if (tool.onMouseMove) tool.onMouseMove(this.toolContext(), pointer);
					if (!this.isDrawing && tool.showsSizePreview) {
						this.drawSizePreview(pointer);
					}
				});

				this.canvas.on('mouse:out', () => {
					if (TOOLS[this.selectedTool].showsSizePreview) {
						this.clearSizePreview();
					}
				});

				this.canvas.on('mouse:down', (event) => {
					const tool = TOOLS[this.selectedTool];
					if (tool.onMouseDown) {
						tool.onMouseDown(this.toolContext(), {
							pointer: this.canvas.getPointer(event.e),
							source: event.e,
						});
					} else if (tool.flags.isDrawingMode) {
						this.isDrawing = true;
					}
				});

				this.canvas.on('mouse:up', (event) => {
					const tool = TOOLS[this.selectedTool];
					if (tool.onMouseUp) {
						tool.onMouseUp(this.toolContext(), this.canvas.getPointer(event.e));
					}
					if (tool.flags.isDrawingMode) {
						this.isDrawing = false;
						this.drawSizePreview(this.canvas.getPointer(event.e));
					}
				});

				// Pen strokes are the one annotation Fabric builds for us, so
				// erasability is attached on completion rather than at creation.
				this.canvas.on('path:created', (event) => {
					event.path.set({ ...ANNOTATION_OBJECT_DEFAULTS });
					this.recordSnapshot();
				});

				// Move, scale and rotate. This was never recorded, which is why
				// undo removed the last drawn object instead of reversing the
				// nudge the user had just made.
				this.canvas.on('object:modified', () => {
					this.recordSnapshot();
				});

				// Grow the box with its content, stopping at the canvas edge,
				// after which Textbox wraps inside the width it has.
				this.canvas.on('text:changed', (event) => {
					const textbox = event.target as Textbox;
					if (!textbox) return;
					fitTextboxWidth(textbox, this.canvasWidth);
				});

				this.canvas.on('text:editing:exited', () => {
					this.canvas.renderAll();
					this.recordSnapshot();
				});

				// Restore focus when existing text objects enter editing mode.
				// This handles edge cases where focus briefly moves to the dialog.
				this.canvas.on('text:editing:entered', (event) => {
					const text = event.target as Textbox;
					if (text && text.isEditing && text.hiddenTextarea) {
						// Brief delay to let any focus changes settle, then restore if needed
						setTimeout(() => {
							if (text.isEditing && text.hiddenTextarea && document.activeElement !== text.hiddenTextarea) {
								text.hiddenTextarea.focus();
							}
						}, 10);
					}
				});

				// Listen for delete/backspace key to remove selected objects
				document.addEventListener('keydown', this.handleKeyDown);

				if (!this.hasResizeListener) {
					window.addEventListener('resize', this.handleResize);
					this.hasResizeListener = true;
				}

				// Baseline for the session, so undoing back to a blank canvas works.
				resetHistory(this.historyState, this.canvas);
				this.canvas.renderAll();
			}

			// Clear initialization flag
			this.isInitializing = false;
		},
		setupBrushes () {
			if (!this.canvas) return;

			this.pencilBrush = new PencilBrush(this.canvas);
			this.pencilBrush.width = this.penSize;
			this.pencilBrush.color = this.hexToRgba(this.selectedColor);

			this.eraserBrush = new EraserBrush(this.canvas);
			this.eraserBrush.width = this.penSize;
			// The alpha of this colour is erase strength, not a tint. It stays
			// fully opaque so an erase always removes the pixel outright.
			this.eraserBrush.color = ERASER_BRUSH_COLOR;

			// The library commits asynchronously and does not await it, so take
			// the commit over: the snapshot has to land after the clip paths are
			// attached, or undo would restore a half-erased canvas.
			this.eraserDisposers.push(this.eraserBrush.on('end', async (event) => {
				event.preventDefault();

				try {
					await this.eraserBrush.commit(event.detail);
				} catch (error) {
					console.warn('Failed to commit an eraser stroke:', error);
					return;
				}

				// Committing is async, so the dialog can close underneath it and
				// destroyCanvas will have nulled the canvas by the time we resume.
				if (!this.canvas) return;

				// Text is untouched by a stroke on purpose. Dragging across a
				// label rubs out what is behind it and leaves the label; only a
				// deliberate click on a box removes it, in the tool's mouse-up.
				this.canvas.renderAll();
				this.recordSnapshot();
			}));

			this.applyBrushForTool();
		},
		applyBrushForTool () {
			if (!this.canvas) return;
			this.canvas.freeDrawingBrush = this.selectedTool === TOOL.ERASER
				? this.eraserBrush
				: this.pencilBrush;
		},
		toolContext (): ToolContext {
			return {
				canvas: this.canvas,
				gesture: this.gesture,
				strokeColor: this.hexToRgba(this.selectedColor),
				size: this.penSize,
				canvasWidth: this.canvasWidth,
				canvasHeight: this.canvasHeight,
				createText: this.createText,
				findTextAt: this.findTextAt,
				focusTextAt: this.focusTextAt,
				removeText: this.removeText,
				recordSnapshot: this.recordSnapshot,
			};
		},
		recordSnapshot () {
			if (!this.canvas) return;
			recordHistorySnapshot(this.historyState, this.canvas);
		},
		selectTool (tool: ToolId) {
			this.selectedTool = tool;
			if (!this.canvas) return;

			applyToolToCanvas(this.canvas, tool);
			this.applyBrushForTool();

			if (!TOOLS[tool].showsSizePreview) {
				this.clearSizePreview();
			}

			// Typing always ends when the tool changes. Leaving a box in edit
			// mode means the next click lands in the text and moves the caret,
			// so switching to Move and clicking the box you were just editing
			// would place the cursor instead of picking the box up.
			this.exitTextEditing();

			// Switching to Move keeps that box selected, so it can be dragged
			// straight away. Every other tool drops the selection, so transform
			// handles do not sit over a canvas that cannot transform anything.
			if (tool !== TOOL.MOVE) {
				this.canvas.discardActiveObject();
			}

			this.canvas.renderAll();
		},
		createText (origin: TextOrigin) {
			if (!this.canvas) return;

			// Commit any box still being typed into before this one exists.
			// setActiveObject below would otherwise exit that box's editing
			// after the new box is already on the canvas, so both would land in
			// one snapshot and a single undo would remove the pair.
			this.exitTextEditing();

			// Textbox rather than IText: it wraps at its own width instead of
			// running off the canvas forever.
			const text = new Textbox('Text', {
				left: origin.left,
				top: origin.top,
				// A starting point only. fitTextboxWidth below shrinks it to the
				// placeholder, and text:changed keeps it fitted while typing, so
				// the box hugs its content instead of running to the canvas edge.
				width: origin.maxWidth,
				fontSize: Math.max(20, this.penSize),
				fill: this.selectedColor,
				fontFamily: 'Arial',
				editable: true,
				...TEXT_OBJECT_DEFAULTS,
			});

			this.canvas.add(text);
			fitTextboxWidth(text, this.canvasWidth);
			this.canvas.setActiveObject(text);
			text.enterEditing();
			this.canvas.renderAll();

			// Deliberately no snapshot here. The box still holds the 'Text'
			// placeholder that selectAll() below is about to replace, so
			// recording now would make undo restore a state the user never
			// typed. text:editing:exited owns the boundary instead, which
			// collapses creation and the first typing session into one step.
			// A later edit of an existing box still records separately,
			// because that box is already in the previous snapshot.

			// Brief delay to ensure focus is on the hidden textarea.
			setTimeout(() => {
				if (text.isEditing && text.hiddenTextarea && document.activeElement !== text.hiddenTextarea) {
					text.hiddenTextarea.focus();
				}
				// Select all text so typing replaces the default "Text" placeholder
				if (text.isEditing) {
					text.selectAll();
				}
			}, 10);
		},
		/**
		 * Topmost text box under the pointer, or null.
		 *
		 * Only text boxes are considered. The Text tool keeps `skipTargetFind`
		 * on, so Fabric never targets anything here: a click in Text mode cannot
		 * select or start dragging a stroke or a shape, and cannot be swallowed
		 * by a near miss on something that is not text.
		 */
		findTextAt (pointer: Point): Textbox | null {
			if (!this.canvas) return null;

			const objects: FabricObject[] = this.canvas.getObjects();
			// Back to front: later objects render on top, so they take the click.
			for (let i = objects.length - 1; i >= 0; i--) {
				const object: FabricObject = objects[i];
				if (object.type === 'textbox' && object.containsPoint(pointer)) {
					return object as Textbox;
				}
			}

			return null;
		},
		focusTextAt (text: Textbox, source: TPointerEvent) {
			if (!this.canvas) return;

			if (!text.isEditing) {
				this.canvas.setActiveObject(text);
				// Fabric commits and exits any other box that was being edited.
				text.enterEditing(source);
			}

			// Runs either way: after entering, and when the user clicks a
			// different spot in the box they are already typing in.
			text.setCursorByClick(source);
			this.canvas.renderAll();
		},
		removeText (text: Textbox) {
			if (!this.canvas) return;

			if (text.isEditing) text.exitEditing();
			this.canvas.remove(text);
			this.canvas.discardActiveObject();
			this.canvas.renderAll();
			this.recordSnapshot();
		},
		exitTextEditing () {
			if (!this.canvas) return;
			const activeObject = this.canvas.getActiveObject();
			if (activeObject && activeObject.type === 'textbox') {
				const textbox = activeObject as Textbox;
				if (textbox.isEditing) {
					textbox.exitEditing();
				}
			}
		},
		handleKeyDown (event: KeyboardEvent) {
			if (!this.canvas) return;

			const activeObject = this.canvas.getActiveObject();

			// While a box is being typed into, every key belongs to the Textbox.
			// This guard is the only thing stopping Backspace from deleting the
			// whole box mid-sentence, and it is matched on the type string, so
			// it has to track the IText-to-Textbox change.
			if (activeObject && activeObject.type === 'textbox') {
				if ((activeObject as Textbox).isEditing) {
					return;
				}
			}

			// This listener is on document, so it also hears the toolbar. The
			// pen-size control is a real input, and Backspace there used to be
			// harmless only because nothing could be selected; Move changes
			// that, and the key would otherwise delete the selected annotation
			// instead of a digit.
			if (this.isDomFieldFocused()) return;

			// Handle Undo: Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
			if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
				event.preventDefault();
				this.undo();
				return;
			}

			// Handle Redo: Ctrl+Y or Ctrl+Shift+Z (Windows/Linux) or Cmd+Shift+Z (Mac)
			if (
				(event.ctrlKey || event.metaKey) &&
				(event.key === 'y' || (event.key === 'z' && event.shiftKey))
			) {
				event.preventDefault();
				this.redo();
				return;
			}

			// Handle Delete or Backspace key
			if (event.key === 'Delete' || event.key === 'Backspace') {
				// Move's marquee can select several objects, so the whole
				// selection goes at once and counts as one history step.
				const selected: FabricObject[] = this.canvas.getActiveObjects();
				if (!selected.length) return;

				event.preventDefault();
				selected.forEach((object: FabricObject) => this.canvas.remove(object));
				this.canvas.discardActiveObject();
				this.canvas.renderAll();
				this.recordSnapshot();
			}
		},
		isDomFieldFocused (): boolean {
			const focused = document.activeElement as HTMLElement | null;
			if (!focused) return false;
			return focused.tagName === 'INPUT' ||
				focused.tagName === 'TEXTAREA' ||
				!!focused.isContentEditable;
		},
		/** Ring under the pointer showing the Pen or Eraser width. */
		drawSizePreview (pointer: { x: number, y: number }) {
			if (!this.canvas || !this.canvas.contextTop) return;

			this.clearSizePreview();
			this.canvas.contextTop.beginPath();
			this.canvas.contextTop.arc(pointer.x, pointer.y, this.penSize / 2, 0, Math.PI * 2, false);
			this.canvas.contextTop.strokeStyle = 'white';
			this.canvas.contextTop.lineWidth = 2;
			this.canvas.contextTop.stroke();
		},
		clearSizePreview () {
			if (!this.canvas || !this.canvas.contextTop) return;
			this.canvas.contextTop.clearRect(0, 0, this.canvas.width, this.canvas.height);
		},
		async undo () {
			if (!this.canvas) return;
			await this.restoreWith(undoSnapshot);
		},
		async redo () {
			if (!this.canvas) return;
			await this.restoreWith(redoSnapshot);
		},
		/**
		 * loadFromJSON recreates every object, so anything holding a reference
		 * to the old ones has to let go first, and the canvas-level flags the
		 * load does not carry have to be put back afterwards.
		 */
		async restoreWith (
			restore: (state: AnnotationHistoryState, canvas: Canvas) => Promise<boolean>,
		) {
			if (!this.canvas) return;

			this.exitTextEditing();
			this.canvas.discardActiveObject();

			try {
				await restore(this.historyState, this.canvas);
			} catch (error) {
				console.warn('Failed to restore an annotation history snapshot:', error);
				return;
			}

			// loadFromJSON is async and slow on a canvas full of erase clip
			// paths, which is ample time for the dialog to close.
			if (!this.canvas) return;

			this.gesture = createToolGestureState();
			this.isDrawing = false;
			applyToolToCanvas(this.canvas, this.selectedTool);
			this.applyBrushForTool();
			this.canvas.renderAll();
		},
		clearAll () {
			if (!this.canvas) return;

			this.exitTextEditing();
			this.canvas.discardActiveObject();
			this.canvas.clear();
			this.canvas.renderAll();
			this.recordSnapshot();
		},
		resetToOriginal () {
			if (!this.hasOriginalImage) return;

			// A session-level decision rather than an annotation edit, so it
			// starts a fresh baseline instead of becoming an undoable step.
			if (this.canvas) {
				this.canvas.clear();
				this.canvas.renderAll();
				resetHistory(this.historyState, this.canvas);
			}
			this.$emit('reset-to-original');
		},
		onDialogOpenStateChange (isOpen: boolean) {
			if (isOpen) {
				this.$nextTick(() => {
					this.initCanvas();
				});
			} else {
				this.onClose();
			}
		},
		/**
		 * Close editor: emits `annotate-cancel` (restore IMGS/IMG snapshot) unless closed after Submit;
		 * after Submit emits `annotate-submit-dismissed` so parents can clear session snapshots.
		 */
		onClose (opts?: { fromSubmit?: boolean }) {
			this.destroyCanvas();

			if (!this.fieldUuid) {
				console.error('VisualPromptEditor: fieldUuid is missing in onClose!');
				return;
			}

			this.updateVisualPromptEditorState({
				fieldUuid: this.fieldUuid,
				editorState: {
					open: false,
					saving: false,
					replaceImageTarget: null,
				},
			});

			if (opts?.fromSubmit) {
				this.$emit('annotate-submit-dismissed');
			} else {
				this.$emit('annotate-cancel');
			}
		},
		updatePromptFieldWithAnnotationText () {
			// Try to find prompt field from fieldsByType
			const promptFields = this.runnitState?.fieldsByType?.[RUNNIT_NODE_FIELD_TYPE.PROMPT];

			let promptField: any = null;

			if (promptFields && promptFields.length > 0) {
				// Use the first prompt field found from fieldsByType
				promptField = promptFields[0];
			} else {
				// Fallback: try selectedNode
				const selectedNode = this.runnitState?.selectedNode;

				if (!selectedNode || typeof selectedNode.nodeDef === 'string') {
					return;
				}

				// Find the prompt field from selectedNode
				const findPromptField = (fields: any[]): any => {
					for (const field of fields) {
						if (field.type === RUNNIT_NODE_FIELD_TYPE.PROMPT) {
							return field;
						}
						if (field.fields) {
							const found = findPromptField(field.fields);
							if (found) return found;
						}
					}
					return null;
				};

				const fields = selectedNode.nodeDef.fields || [];
				promptField = findPromptField(fields);
				if (!promptField) {
					return;
				}
			}

			this.updatePromptValue(promptField);
		},
		updatePromptValue (promptField: any) {
			// Get current prompt value
			const currentPromptValue = this.runnitState?.inputValues?.[promptField.fieldDefUuid];
			const currentPromptString = extractPromptStringValue(currentPromptValue) || '';

			// Prepend the annotation text (only if not already present)
			const annotationText = 'Follow and remove the annotations marked on the image;';
			let updatedPromptString: string;

			// If there's no existing prompt, just use the annotation text
			if (!currentPromptString.trim()) {
				updatedPromptString = annotationText;
			} else {
				// Check if annotation text is already at the start
				const alreadyHasAnnotation = currentPromptString.trim().toLowerCase().startsWith(annotationText.toLowerCase());

				if (!alreadyHasAnnotation) {
					updatedPromptString = `${annotationText} ${currentPromptString}`.trim();
				} else {
					updatedPromptString = currentPromptString;
				}
			}

			// Update the prompt value
			const updatedPromptValue = {
				...currentPromptValue,
				value: updatedPromptString,
			};

			// Update runnitState
			this.updateRunnitState({
				inputValues: {
					...this.runnitState.inputValues,
					[promptField.fieldDefUuid]: updatedPromptValue,
				},
			});
		},
		async handleDone () {
			try {
				await this.updateVisualPromptEditorState({
					fieldUuid: this.fieldUuid || 'default',
					editorState: {
						saving: true,
					},
				});

				if (!this.canvas) {
					this.onClose({ fromSubmit: true });
					return;
				}

				// Check if there are any annotations
				if (this.canvas.getObjects().length === 0) {
					// No annotations, just close
					this.onClose({ fromSubmit: true });
					return;
				}

				const source = this.fieldEditorState.baseSource;
				if (!source) {
					throw new AnnotationSaveError(
						ANNOTATION_SAVE_FAILURE.NO_BACKGROUND_IMAGE,
						'No base image reference to composite onto',
					);
				}

				// Only the editor-sized annotation layer leaves the browser. Firebase
				// loads the full-resolution base, composites and stores the result.
				const request: AnnotationSaveRequest = {
					source,
					overlayPngBase64: this.canvas.toCanvasElement().toDataURL('image/png').split(',')[1],
				};
				const functionRef = functions.httpsCallable('saveAnnotatedImage', { timeout: ANNOTATION_CLIENT_TIMEOUT_MS });
				const data: AnnotationSaveResponse = (await functionRef(request)).data;
				if ('error' in data) {
					throw new AnnotationSaveError(data.error.code, data.error.message, undefined, { trustedDetail: data.error.message });
				}
				const { createdAtMs, ...uploadRecord } = data.upload;
				// DbTimestamp is Timestamp & Date, so a real Timestamp still needs the assertion.
				const upload = { ...uploadRecord, createdAt: Timestamp.fromMillis(createdAtMs) } as RunnitUpload;

				// Emit the new image value first
				if (this.isReplacementMode) {
					this.$emit('replace-image', {
						upload,
						replaceImageTarget: this.fieldEditorState.replaceImageTarget,
					});
				} else {
					// Normal mode: emit as input (replaces entire value)
					this.$emit('input', { upload });
				}

				// Update prompt field value AFTER image replacement is processed
				// Use $nextTick to ensure the image input state update has completed
				this.$nextTick(() => {
					this.updatePromptFieldWithAnnotationText();
				});

				this.onClose({ fromSubmit: true });

			} catch (err) {
				const saveError: AnnotationSaveError = toAnnotationSaveError(err);

				// Keep the raw error in the console for a live debugging session, and
				// mirror the classified reason so a screenshot of the console is
				// enough to tell a local failure apart from a server one.
				console.error(`Error saving annotations [${saveError.reason}]`, saveError.message, err);

				// Without this, a failure leaves no trace anywhere: the snackbar is
				// gone in seconds and nothing reaches our analytics. These properties
				// are what let us diagnose a report like "it just says error saving".
				analytics.track(EVENTS.ANNOTATION_SAVE_FAILED, {
					reason: saveError.reason,
					detail: saveError.message,
					image_width: this.imageWidth || null,
					image_height: this.imageHeight || null,
					annotation_count: this.canvas ? this.canvas.getObjects().length : 0,
					is_replacement_mode: this.isReplacementMode,
					field_uuid: this.fieldUuid || null,
				});

				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: getAnnotationSaveErrorMessage(saveError),
					show: true,
				});
			} finally {
				await this.updateVisualPromptEditorState({
					fieldUuid: this.fieldUuid || 'default',
					editorState: {
						saving: false,
					},
				});
			}
		},
		handleResize () {
			if (this.canvas) {
				this.canvas.setDimensions({
					width: this.canvasWidth,
					height: this.canvasHeight,
				});
				this.canvas.renderAll();
			}
		},
		destroyCanvas () {
			// Remove keyboard listener
			document.removeEventListener('keydown', this.handleKeyDown);

			// Reset any in-progress shape drawing
			this.gesture = createToolGestureState();
			this.isDrawing = false;

			// The eraser hands back disposers rather than named canvas events,
			// and it owns an offscreen effect canvas. This runs on every dialog
			// close, not just on destroy, so neither may be skipped.
			this.eraserDisposers.forEach((dispose: () => void) => dispose());
			this.eraserDisposers = [];

			// Snapshots are whole-canvas JSON and grow once erasing adds
			// clip-path data per object, so up to SNAPSHOT_LIMIT of them would
			// otherwise outlive the dialog for the component's whole lifetime.
			this.historyState = createAnnotationHistoryState();
			if (this.eraserBrush) {
				this.eraserBrush.dispose();
				this.eraserBrush = null;
			}

			const activeCanvas = this.canvas;
			if (activeCanvas) {
				const lowerCanvasElement = activeCanvas.elements.lower.el;
				const canvasContainer = getFabricCanvasContainer(activeCanvas);

				activeCanvas.off('mouse:move');
				activeCanvas.off('mouse:out');
				activeCanvas.off('mouse:down');
				activeCanvas.off('mouse:up');
				activeCanvas.off('path:created');
				activeCanvas.off('object:modified');
				activeCanvas.off('text:changed');
				activeCanvas.off('text:editing:exited');
				activeCanvas.off('text:editing:entered');

				activeCanvas.clear();
				disposeFabricCanvasSafely(activeCanvas);
				restoreFabricCanvasElement(lowerCanvasElement, canvasContainer);
				this.canvas = null;
				this.pencilBrush = null;
			}

			const canvasElement = (this.$refs.canvasRef as HTMLCanvasElement | undefined) ||
				document.getElementById(this.canvasId) as HTMLCanvasElement | null;
			if (canvasElement) {
				restoreFabricCanvasElement(canvasElement);
			}

			// Reset initialization flag
			this.isInitializing = false;
		},
		aspectRatio () {
			if (this.imageWidth && this.imageHeight) {
				return this.imageWidth / this.imageHeight;
			}
			return 1;
		},
		isLandscape () {
			return this.aspectRatio() >= 1;
		},
		setCanvasHeight (): number {
			const height = this.$vuetify.breakpoint.xsOnly ? this.MOBILE_STATIC_HEIGHT :
				this.$vuetify.breakpoint.lgAndUp ? this.LARGE_STATIC_HEIGHT : this.STATIC_HEIGHT
			this.canvasHeight = this.isLandscape() ? height / this.aspectRatio() : height;
			return this.canvasHeight;
		},
		setCanvasWidth (): number {
			const width = this.$vuetify.breakpoint.xsOnly ? this.MOBILE_STATIC_WIDTH :
				this.$vuetify.breakpoint.lgAndUp ? this.LARGE_STATIC_WIDTH : this.STATIC_WIDTH
			this.canvasWidth = this.isLandscape() ? width : width * this.aspectRatio();
			return this.canvasWidth;
		},
		imageLoaded (event: any) {
			this.imageWidth = this.$refs.imageRef.naturalWidth;
			this.imageHeight = this.$refs.imageRef.naturalHeight;

			// Only initialize if canvas doesn't exist and we're not already initializing
			// Also check if the dialog is actually open before initializing
			if (!this.canvas && !this.isInitializing && this.fieldEditorState?.open) {
				this.initCanvas();
			}
		},
	},
	beforeDestroy () {
		this.hasResizeListener = false;
		window.removeEventListener('resize', this.handleResize);
		this.destroyCanvas();
	},
	components: {
		BaseSlider,
		GlassButton,
		ColorInput,
		ComplexBackground,
		DialogOrBottomSheet,
	},
});
